<template>
    <div class="ls-flow--container">
        <LsTemplatedLoading :show="loading" :full-screen="true" test-id="loading" />
        <div v-if="!loading" test-id="form_wrapper">
            <div class="ls-flow--title">Confirm Change Service Window</div>
            <form novalidate @submit.prevent="createScheduleChangeServiceWindow">
                <LsInfoCard>
                    This action will start a distribution to look for a new pro for this service on
                    the date chosen. In case we cannot find the provider for the date chosen below,
                    we will keep the customer's schedule as is.
                </LsInfoCard>

                <LsSelect
                    v-model="customerStartsAtSelection"
                    track-id="change_service_window_view-customer_starts_at"
                    name="customer_starts_at"
                    :options="customerStartsAtOptions"
                    label="Select customer's window"
                    class="ls-flow--330"
                    :validation="{ required: true }"
                />

                <LsInput
                    v-model="zendesk_ticket_id"
                    track-id="change_service_window_view-zendesk_ticket_id"
                    label="Zendesk ticket number"
                    name="zendesk_ticket_id"
                    type="number"
                    placeholder="Enter number"
                    :validation="{ required: true, min_value: 1000000, max: 8 }"
                />

                <LsError v-if="isInvalid" />

                <LsButton
                    track-id="change_service_window_view-confirm_change"
                    text="Confirm Change Service Window"
                    :loading="isUpdating"
                    type="submit"
                />
            </form>
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment-timezone';
import gql from 'graphql-tag';
import { lsErrorService, lsFilters, lsModalService } from 'ls-vue-common';
import { lsHelpers } from '@/helpers';
import { apolloClient } from '@/vue-apollo';
import ScheduleChangeServiceWindow from '@/graphql/queries/ScheduleChangeServiceWindow.gql';
import Schedule from '@/graphql/queries/Schedule.gql';
import CreateScheduleChangeServiceWindow from '@/graphql/mutations/CreateScheduleChangeServiceWindow.gql';
import { LsButton, LsInput, LsInfoCard, LsSelect } from '@/components/common';
import { FlowSuccessModal } from '@/modals';
import LsTemplatedLoading from '@/components/LsTemplatedLoading/LsTemplatedLoading.vue';
import LsError from '@/components/common/LsError/LsError.vue';

export default {
    name: 'ChangeServiceWindowView',
    components: {
        LsButton,
        LsInput,
        LsSelect,
        LsTemplatedLoading,
        LsError,
        LsInfoCard,
    },
    $_veeValidate: {
        validator: 'new',
    },
    data() {
        return {
            loading: false,
            isUpdating: false,
            isInvalid: false,
            customerStartsAtOptions: [],
            customerStartsAtSelection: null,
            zendesk_ticket_id: '',

            property: apolloClient.readFragment({
                id: `Property:${this.$route.params.property_id}`,
                fragment: gql`
                    fragment property on Property {
                        street
                    }
                `,
            }),
        };
    },
    apollo: {
        scheduleChangeServiceWindow: {
            query: ScheduleChangeServiceWindow,
            variables() {
                return {
                    customer_id: _.toNumber(this.$route.params.customer_id),
                    property_id: _.toNumber(this.$route.params.property_id),
                    schedule_id: _.toNumber(this.$route.params.schedule_id),
                };
            },
            result({ data, loading }) {
                if (!loading) {
                    this.customerStartsAtOptions = _.map(
                        data.scheduleChangeServiceWindow,
                        (item) => {
                            const { customer_starts_at, customer_ends_at } = item;
                            const startsAt = moment(customer_starts_at).format('ddd MM/DD');
                            const endsAt = moment(customer_ends_at).format('ddd MM/DD');

                            return {
                                label: `${startsAt} - ${endsAt}`,
                                value: customer_starts_at,
                            };
                        }
                    );
                }
            },
            error(error) {
                lsErrorService.log({ error, showAlert: true });
                lsHelpers.goToParentView({ replace: true });
            },
            loadingKey: 'loading',
            fetchPolicy: 'network-only',
        },
    },
    methods: {
        async createScheduleChangeServiceWindow() {
            const valid = await this.$validator.validateAll();
            this.isInvalid = !valid;
            if (!valid) {
                return;
            }

            this.isUpdating = true;
            this.$apollo
                .mutate({
                    mutation: CreateScheduleChangeServiceWindow,
                    variables: {
                        customer_id: _.toNumber(this.$route.params.customer_id),
                        property_id: _.toNumber(this.$route.params.property_id),
                        schedule_id: _.toNumber(this.$route.params.schedule_id),
                        customer_starts_at: this.customerStartsAtSelection,
                        zendesk_ticket_id: this.zendesk_ticket_id
                            ? _.toInteger(this.zendesk_ticket_id)
                            : null,
                    },
                    refetchQueries: [
                        {
                            query: Schedule,
                            variables: {
                                customer_id: _.toNumber(this.$route.params.customer_id),
                                property_id: _.toNumber(this.$route.params.property_id),
                                schedule_id: _.toNumber(this.$route.params.schedule_id),
                            },
                        },
                    ],
                })
                .then(({ data }) => {
                    const { copy, schedule, request } = data.createScheduleChangeServiceWindow;
                    const { next_customer_starts_at, next_customer_ends_at } =
                        schedule || request.schedule;
                    const {
                        property: { street },
                    } = this;
                    const weekDay = moment(next_customer_starts_at).format('dddd');
                    const startingDate = lsFilters.date({
                        date: next_customer_starts_at,
                        format: 'monthDateYear',
                    });
                    let title = `All services at ${street} have been moved to ${weekDay} starting ${startingDate}.`;

                    lsModalService.open({
                        parent: this,
                        component: FlowSuccessModal,
                        props: {
                            title,
                            copy,
                            displayServicesOnDate: {
                                next_customer_starts_at,
                                next_customer_ends_at,
                            },
                        },
                    });
                })
                .catch((error) => {
                    lsErrorService.log({ error, showAlert: true });
                })
                .finally(() => {
                    this.isUpdating = false;
                });
        },
    },
};
</script>
